import { App, Alert, Card, Input, Space, Table, Tag, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  INDUSTRIAL_REF_BOOK_PAGE_SIZE,
  useIndustrialRefBookLibrary,
} from '@/hooks/useIndustrialRefBookLibrary';
import { ROUTES } from '@/routes/paths';
import { useIndustrialFocus } from '@/stores/IndustrialFocusContext';
import type { IndustrialRefBookRecord } from '@/types/industrialRefBook';

const COORD_DECIMALS = 5;

export default function IndustrialRefBookPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { focus } = useIndustrialFocus();
  const lib = useIndustrialRefBookLibrary();

  const columns = useMemo(() => buildColumns(t), [t]);

  const handleRowClick = (record: IndustrialRefBookRecord) => {
    focus(record.id, record.latitude, record.longitude);
    message.info(t('industrialRefBook.library.openingOnMap', { title: record.title }));
    navigate(ROUTES.ROUTES);
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        {t('industrialRefBook.library.title')}
      </Typography.Title>
      <Input.Search
        allowClear
        value={lib.search}
        onChange={(e) => lib.setSearch(e.target.value)}
        placeholder={t('industrialRefBook.library.searchPlaceholder')}
        style={{ maxWidth: 480 }}
      />
      {lib.status === 'error' && (
        <Alert
          type="error"
          showIcon
          message={t('industrialRefBook.library.loadError')}
          description={lib.errorMessage ?? undefined}
        />
      )}
      <Card size="small" styles={{ body: { padding: 0 } }}>
        <Table<IndustrialRefBookRecord>
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={lib.items as IndustrialRefBookRecord[]}
          loading={lib.status === 'loading'}
          pagination={{
            current: lib.page,
            pageSize: INDUSTRIAL_REF_BOOK_PAGE_SIZE,
            total: lib.total,
            showSizeChanger: false,
            size: 'small',
            onChange: lib.setPage,
            showTotal: (total, range) =>
              t('industrialRefBook.library.pagination', {
                from: range[0],
                to: range[1],
                total,
              }),
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </Space>
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function buildColumns(t: Translate): TableColumnsType<IndustrialRefBookRecord> {
  const dash = <Typography.Text type="secondary">—</Typography.Text>;
  const opt = (v: string | number | null) =>
    v === null || v === '' ? dash : <span>{v}</span>;
  return [
    { title: t('industrialRefBook.columns.id'), dataIndex: 'id', key: 'id', width: 70,
      render: (v: number) => <Typography.Text code style={{ fontSize: 11 }}>{v}</Typography.Text> },
    { title: t('industrialRefBook.columns.title'), dataIndex: 'title', key: 'title',
      width: 260, ellipsis: true },
    { title: t('industrialRefBook.columns.short_title'), dataIndex: 'short_title',
      key: 'short_title', width: 140, ellipsis: true, render: (v: string | null) => opt(v) },
    { title: t('industrialRefBook.columns.type'), dataIndex: 'type', key: 'type', width: 120,
      render: (v: string) => <Tag style={{ margin: 0 }}>{t(`industrialRefBook.type.${v}`, { defaultValue: v })}</Tag> },
    { title: t('industrialRefBook.columns.address'), dataIndex: 'address', key: 'address',
      width: 320, ellipsis: true },
    { title: t('industrialRefBook.columns.region_id'), dataIndex: 'region_id', key: 'region_id', width: 90 },
    { title: t('industrialRefBook.columns.inn'), dataIndex: 'inn', key: 'inn', width: 120,
      render: (v: string | null) => opt(v) },
    { title: t('industrialRefBook.columns.is_company'), dataIndex: 'is_company', key: 'is_company',
      width: 90, align: 'center',
      render: (v: boolean) => (v ? <Tag color="blue" style={{ margin: 0 }}>{t('common.yes')}</Tag> : dash) },
    { title: t('industrialRefBook.columns.year'), dataIndex: 'year', key: 'year', width: 80,
      render: (v: number | null) => opt(v) },
    { title: t('industrialRefBook.columns.timezone'), dataIndex: 'timezone', key: 'timezone', width: 130 },
    { title: t('industrialRefBook.columns.post_index'), dataIndex: 'post_index', key: 'post_index',
      width: 90, render: (v: string | null) => opt(v) },
    { title: t('industrialRefBook.columns.fias_id'), dataIndex: 'fias_id', key: 'fias_id',
      width: 130, ellipsis: true, render: (v: string | null) => opt(v) },
    { title: t('industrialRefBook.columns.rayon_fias_id'), dataIndex: 'rayon_fias_id',
      key: 'rayon_fias_id', width: 130, ellipsis: true, render: (v: string | null) => opt(v) },
    { title: t('industrialRefBook.columns.geohash'), dataIndex: 'geohash', key: 'geohash',
      width: 110, render: (v: string | null) => opt(v) },
    { title: t('industrialRefBook.columns.coords'), key: 'coords', width: 160,
      render: (_v, r) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {r.latitude.toFixed(COORD_DECIMALS)}, {r.longitude.toFixed(COORD_DECIMALS)}
        </Typography.Text>
      ) },
  ];
}
